import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Sparkles, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSelector from "@/components/LanguageSelector";
import ChatWidget from "@/components/ChatWidget";
import { STRINGS, isRtl } from "@/lib/i18n";
import { trackVisit } from "@/lib/api";

export default function LandingPage() {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "tr");
  const t = STRINGS[lang];
  const rtl = isRtl(lang);

  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = rtl ? "rtl" : "ltr";
  }, [lang, rtl]);

  // Track unique visit per browser session (sessionStorage prevents double-counting on refresh)
  useEffect(() => {
    if (!sessionStorage.getItem("visit_tracked")) {
      trackVisit();
      sessionStorage.setItem("visit_tracked", "1");
    }
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="landing-page">
      {/* Preview-only banner */}
      <div className="relative z-30 bg-amber-50 border-b border-amber-200 text-amber-900 text-xs sm:text-sm" data-testid="preview-banner">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <strong>Önizleme:</strong> Bu sayfa AI asistanın test ortamıdır. Gerçek site:
            {" "}
            <a href="https://www.drhuseyintirman.com" target="_blank" rel="noreferrer" className="underline font-medium hover:text-amber-700">
              drhuseyintirman.com
            </a>
            {" "}— widget oraya gömülerek çalışır.
          </div>
        </div>
      </div>

      {/* Subtle background texture */}
      <div className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background:
            "radial-gradient(800px 400px at 20% 0%, rgba(179,155,122,0.18), transparent 60%), radial-gradient(700px 500px at 90% 100%, rgba(139,155,136,0.14), transparent 60%)",
        }}
      />

      {/* Header */}
      <header className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #B39B7A, #8B9B88)" }}>
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-serif text-lg leading-none">Dr. Hüseyin Tırman</div>
            <div className="font-display text-xs text-muted-foreground tracking-wider uppercase">Aesthetic Medicine</div>
          </div>
        </div>
        <nav className="hidden md:flex items-center gap-8 font-display text-sm">
          <a href="#services" className="hover:text-primary transition-colors" data-testid="nav-services">{t.nav_treatments}</a>
          <a href="#about" className="hover:text-primary transition-colors" data-testid="nav-about">{t.nav_about}</a>
          <a href="#contact" className="hover:text-primary transition-colors" data-testid="nav-contact">{t.nav_contact}</a>
        </nav>
        <LanguageSelector value={lang} onChange={setLang} />
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-12 pb-24 lg:pt-24 lg:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="font-display text-xs tracking-[0.3em] text-muted-foreground mb-6">{t.hero_kicker}</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-foreground" data-testid="hero-title">
            {t.hero_title.split("\n").map((line, i) => (
              <span key={i} className="block">{line}</span>
            ))}
          </h1>
          <p className="mt-8 text-base sm:text-lg max-w-xl text-muted-foreground font-body">{t.hero_sub}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Button
              data-testid="hero-cta"
              size="lg"
              onClick={() => {
                // Trigger orb open by dispatching custom event - simpler: scroll & let user click
                const orb = document.querySelector('[data-testid="chat-orb"]');
                if (orb) orb.click();
              }}
              className="rounded-full px-8 h-12 font-display"
              style={{ background: "#2C3531" }}
            >
              <Sparkles className="w-4 h-4 mr-2" /> {t.hero_cta}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full px-8 h-12 font-display border-foreground/20"
              data-testid="hero-secondary"
            >
              {t.hero_secondary}
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="mt-24 flex items-center gap-2 text-muted-foreground"
        >
          <ChevronDown className="w-4 h-4 animate-bounce" />
          <span className="text-xs font-display tracking-widest uppercase">scroll</span>
        </motion.div>
      </section>

      {/* Services */}
      <section id="services" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="font-display text-xs tracking-[0.3em] text-muted-foreground mb-3">02</div>
        <h2 className="font-serif text-4xl sm:text-5xl mb-16">{t.section_services}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border/60">
          {t.services.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-background p-10 lg:p-14 group cursor-default"
              data-testid={`service-card-${i}`}
            >
              <div className="font-display text-xs text-muted-foreground tracking-widest">0{i + 1}</div>
              <h3 className="font-serif text-3xl mt-3 mb-4 group-hover:text-primary transition-colors">{s.title}</h3>
              <p className="text-muted-foreground font-body leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* About */}
      <section id="about" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <div className="font-display text-xs tracking-[0.3em] text-muted-foreground mb-3">03</div>
            <h2 className="font-serif text-4xl sm:text-5xl mb-6">{t.section_about_title}</h2>
            <p className="text-muted-foreground font-body text-lg leading-relaxed">{t.section_about_text}</p>
          </div>
          <div className="aspect-[4/5] rounded-3xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1759262151080-e05ba1c6294f?crop=entropy&cs=srgb&fm=jpg&w=900"
              alt="Clinic interior"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 border-t border-border/40">
        <div className="font-display text-xs tracking-[0.3em] text-muted-foreground mb-3">04</div>
        <h2 className="font-serif text-4xl sm:text-5xl mb-12">{t.contact_title}</h2>
        <div className="grid sm:grid-cols-3 gap-8">
          <a href="tel:+902122323748" className="flex items-start gap-4 group" data-testid="contact-phone">
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
              <Phone className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-display">{t.contact_phone}</div>
              <div className="font-serif text-lg mt-1">+90 212 232 37 48</div>
            </div>
          </a>
          <a href="mailto:info@dr-ht.com" className="flex items-start gap-4 group" data-testid="contact-email">
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-display">{t.contact_email}</div>
              <div className="font-serif text-lg mt-1">info@dr-ht.com</div>
            </div>
          </a>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
              <MapPin className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-display">{t.contact_address}</div>
              <div className="font-serif text-lg mt-1">{t.address}</div>
            </div>
          </div>
        </div>
      </section>

      <footer className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-10 border-t border-border/40 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
        <div>© {new Date().getFullYear()} Dr. Hüseyin Tırman</div>
        <a href="/admin" className="font-display text-xs tracking-widest uppercase hover:text-primary" data-testid="admin-link">Admin</a>
      </footer>

      <ChatWidget lang={lang} setLang={setLang} />
    </div>
  );
}
